/**
 * ApproxStartEstimator · iter303ak-fix23f — Accepted · T10 · Action 15
 * ─────────────────────────────────────────────────────────────────────
 * Placeholder heuristic estimator for approximate start times. Sits
 * between the Confirm-Decisions step (A10) and the Send-Bundle step
 * (A20). Reads accepted quote types, evaluates the admin-editable
 * heuristic rules (via /app-settings/approx-start-heuristics/evaluate),
 * and pre-fills the operator's dropdown with the winning preset. The
 * operator can pick a different preset or type a custom string.
 *
 * Writes `approx_start_time` (string) onto the appraisal so the
 * downstream Acceptance Summary letter (T10 A20) can render it.
 *
 * The "Edit heuristics" link deep-jumps admins to
 * /admin/approx-start-heuristics for tuning the buckets + rules.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CalendarClock, ExternalLink, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const CUSTOM_KEY = '__custom__';

export default function ApproxStartEstimator({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const appraisalId = capturedData?.appraisal_id;
  const app = capturedData?.appraisal || capturedData?.appraisal_record || {};

  // Derive accepted quote types from upstream decisions (T10 A10 or
  // Follow-up A20). Falls back to the appraisal's primary quote types
  // if decisions aren't in captured_data (legacy path).
  const acceptedQuoteTypes = useMemo(() => {
    const decisions = capturedData?.decisions
      || capturedData?.await_quote_response?.decisions
      || app?.child_decisions
      || {};
    const list = Object.values(decisions)
      .filter((d) => d && d.decision === 'accepted' && d.quote_type_ref)
      .map((d) => d.quote_type_ref);
    if (list.length > 0) return Array.from(new Set(list));
    // Legacy fallback — no decisions map, use parent's primary_quote_types.
    return app?.primary_quote_types || [];
  }, [capturedData, app]);

  const [heuristics, setHeuristics] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [presetKey, setPresetKey] = useState(value?.preset_key || app?.approx_start_preset_key || '');
  const [customText, setCustomText] = useState(value?.approx_start_time && !value?.preset_key ? value.approx_start_time : (app?.approx_start_time && !app?.approx_start_preset_key ? app.approx_start_time : ''));
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [{ data: cfg }, { data: sug }] = await Promise.all([
        api.get('/app-settings/approx-start-heuristics'),
        api.post('/app-settings/approx-start-heuristics/evaluate', { accepted_quote_types: acceptedQuoteTypes }),
      ]);
      setHeuristics(cfg);
      setSuggestion(sug);
      // Auto-select the suggested preset if the operator hasn't chosen anything yet.
      if (!presetKey && !customText) {
        setPresetKey(sug?.preset_key || '');
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed to load heuristics');
    } finally { setLoading(false); }
  }, [acceptedQuoteTypes, presetKey, customText]);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [acceptedQuoteTypes.join(',')]);

  // Derived label + text for downstream.
  const activePreset = useMemo(() => {
    if (presetKey === CUSTOM_KEY) return null;
    return (heuristics?.presets || []).find((p) => p.key === presetKey) || null;
  }, [presetKey, heuristics]);

  const approxText = useMemo(() => {
    if (presetKey === CUSTOM_KEY) return customText.trim();
    return activePreset?.label || '';
  }, [presetKey, customText, activePreset]);

  useEffect(() => {
    onChange && onChange({
      approx_start_time: approxText,
      preset_key: presetKey === CUSTOM_KEY ? null : presetKey,
    });
  }, [approxText, presetKey, onChange]);

  const openAdmin = () => {
    window.open('/admin/approx-start-heuristics', '_blank', 'noopener,noreferrer');
  };

  const advance = async () => {
    if (!approxText) {
      setErr('Pick a bucket or type a custom start-time string.');
      return;
    }
    setSaving(true); setErr('');
    try {
      if (appraisalId) {
        await api.patch(`/appraisals/${appraisalId}`, {
          approx_start_time: approxText,
          approx_start_preset_key: presetKey === CUSTOM_KEY ? null : presetKey,
        });
      }
      onNext();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="wf-approx-start-estimator">
      <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-4">
        <div className="flex items-start gap-3">
          <CalendarClock className="w-5 h-5 text-sky-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-sky-900">Approximate start time</div>
            <div className="text-sm text-sky-800 mt-1">
              {params.prompt || 'Pick an approximate start-time bucket for the accepted quote(s). This shows up in the Acceptance Summary letter so the client has a rough idea. Not a firm date — the full scheduler will replace this later.'}
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="p-6 text-sm text-slate-400 italic inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading heuristics…
        </div>
      )}

      {!loading && suggestion && (
        <div className="p-3 rounded-md border border-emerald-200 bg-emerald-50/50 text-xs text-emerald-800 flex items-start gap-2" data-testid="wf-approx-start-suggestion">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div>
              <b>Suggested:</b> {suggestion.label} <span className="text-emerald-600">(rule: <i>{suggestion.rule_matched}</i> · season: {suggestion.season})</span>
            </div>
            {acceptedQuoteTypes.length > 0 && (
              <div className="mt-0.5 text-[11px] text-emerald-700">
                Based on accepted quote types: {acceptedQuoteTypes.join(', ')}
              </div>
            )}
          </div>
        </div>
      )}

      {!loading && heuristics && (
        <div className="p-4 border border-slate-200 rounded-lg bg-white space-y-3">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Choose a bucket</div>
          <div className="flex flex-wrap gap-2" data-testid="wf-approx-start-presets">
            {(heuristics.presets || []).map((p) => {
              const active = presetKey === p.key;
              const activeColor = {
                rose:   'bg-rose-600 border-rose-600 text-white',
                amber:  'bg-amber-500 border-amber-500 text-white',
                orange: 'bg-orange-500 border-orange-500 text-white',
                purple: 'bg-purple-600 border-purple-600 text-white',
                sky:    'bg-sky-600 border-sky-600 text-white',
              }[p.color] || 'bg-slate-700 border-slate-700 text-white';
              return (
                <button
                  key={p.key}
                  onClick={() => { setPresetKey(p.key); setCustomText(''); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${active ? activeColor : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-50'}`}
                  data-testid={`wf-approx-start-preset-${p.key}`}
                >
                  {p.label}
                </button>
              );
            })}
            <button
              onClick={() => setPresetKey(CUSTOM_KEY)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${presetKey === CUSTOM_KEY ? 'bg-slate-800 border-slate-800 text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-50'}`}
              data-testid="wf-approx-start-preset-custom"
            >
              ✏️ Custom…
            </button>
          </div>

          {presetKey === CUSTOM_KEY && (
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Custom start-time string</label>
              <input
                type="text"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                placeholder="e.g. mid-March 2026 (weather permitting)"
                className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                data-testid="wf-approx-start-custom-input"
              />
            </div>
          )}

          <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
            <div className="text-slate-500">
              Selected: <b className="text-slate-700">{approxText || '(none)'}</b>
            </div>
            <button
              onClick={openAdmin}
              className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900 underline decoration-dotted"
              data-testid="wf-approx-start-open-admin"
              title="Open the admin editor to tweak the heuristic rules + preset buckets"
            >
              <ExternalLink className="w-3 h-3" /> Edit heuristics in admin
            </button>
          </div>
        </div>
      )}

      {err && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3">
          <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-approx-start-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && (
            <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-approx-start-cancel">Cancel</button>
          )}
          <button
            onClick={advance}
            disabled={saving || !approxText}
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-approx-start-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> {saving ? 'Saving…' : 'Save & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
